import React from "react";
import { AsyncPaginate } from "react-select-async-paginate";
import { SingleValue, components } from "react-select";
import { getAllOrganizations } from "../../../../setup/api/services/organization";
import { IOrganizations } from "../../../../setup/types/Organisation";
import styles from "./styles.module.scss";

const pageSize = 10;

type OrganizationOption = {
  value: string;
  label: string;
};

type OrganizationSelectProps = {
  value: OrganizationOption;
  onChange: (newValue: SingleValue<OrganizationOption>) => void;
  t: (key: string) => string; // For translations
};

const OrganizationSelect: React.FC<OrganizationSelectProps> = ({
  value,
  onChange,
  t,
}) => {
  const loadOptions = async (q: string, _, additional) => {
    const waffyOption = {
      value: "WAFFY",
      label: "WAFFY",
    };
    const response = await getAllOrganizations(additional.page, pageSize, q);
    const options = response.data.content.map((org: IOrganizations) => ({
      value: org.code,
      label: org.name,
    }));

    if (additional.page === 0) {
      options.unshift(waffyOption);
      options.unshift({ value: "", label: "الكل" });
    }

    return {
      options: options,
      hasMore: response.data.totalPages > additional.page + 1,
      additional: {
        page: additional.page + 1,
      },
    };
  };

  const handleOrganizationSelected = (
    newValue: SingleValue<OrganizationOption>
  ) => {
    if (!newValue) {
      onChange({ value: "", label: "الكل" });
      return;
    }
    onChange(newValue);
  };

  // Custom DropdownIndicator using image
  const CustomDropdownIndicator = (props: any) => {
    return (
      <components.DropdownIndicator {...props}>
        <img
          src="/assets/images/svg/drop_down_arrow.svg"
          alt="Toggle Dropdown"
          style={{ width: 14, height: 14 }} // adjust size as needed
        />
      </components.DropdownIndicator>
    );
  };

  return (
    <AsyncPaginate
      additional={{ page: 0 }}
      value={value}
      loadOptions={loadOptions}
      onChange={handleOrganizationSelected}
      placeholder={t("lbl_choose_organisation")}
      formatOptionLabel={(option, { context }) => {
        if (context === "menu") {
          // Only show the main label in the dropdown menu
          return <div>{option.label}</div>;
        } else {
          // Show the label + extra label in the selected input
          return (
            <div className={styles.labelContainer}>
              <p className={styles.label}>{`${t("lbl_organization")}:`}</p>
              {option.label}
            </div>
          );
        }
      }}
      components={{
        DropdownIndicator: CustomDropdownIndicator,
      }}
      styles={{
        control: (base, state) => ({
          ...base,
          minWidth: "144px",
          height: "40px",
          padding: "0 12px",
          border: "1px solid #DDE1ED",
          borderRadius: "10px",
          width: "max-content",
          cursor: "pointer",
          boxShadow: "none", // remove blue focus shadow
          borderColor: state.isFocused ? "#DDE1ED" : "#DDE1ED", // keep a neutral border
          "&:hover": {
            borderColor: "#DDE1ED", // remove hover color if you want
          },
        }),
        input: (base) => ({
          ...base,
          fontSize: "0.75rem",
          margin: 0,
          padding: 0,
        }),
        placeholder: (base) => ({
          ...base,
          fontSize: "0.75rem",
        }),
        valueContainer: (base) => ({
          ...base,
          padding: 0, // remove padding around single value
          height: "100%",
        }),
        singleValue: (base) => ({
          ...base,
          fontSize: "0.75rem",
          height: "100%",
        }),
        indicatorsContainer: (base) => ({
          ...base,
          padding: 0,
        }),
        indicatorSeparator: () => ({
          display: "none",
        }),
        dropdownIndicator: (base) => ({
          ...base,
          padding: 0,
        }),
        clearIndicator: (base) => ({
          ...base,
          padding: 0,
        }),
        option: (base, state) => ({
          ...base,
          backgroundColor: state.isSelected
            ? "#f2f2f2" // selected option background color
            : state.isFocused
            ? "#f2f2f2" // hovered/focused option background
            : "white",
          fontSize: "1rem",
          fontWeight: state.isSelected ? 600 : 400,
          color: "#181b23",
          cursor: "pointer",
        }),
      }}
    />
  );
};

export default OrganizationSelect;
